import { PrimitiveType } from "@buildwithflux/models";
import { geom2 } from "../../../common";
import { mat3 } from "../../../common/geometry/mat3";
import { approxAABoundingBox, boundingBoxDistance } from "./approxAABoundingBox";
import { approxBoundingCircle, boundingCircleDistance } from "./approxBoundingCircle";
import { approxFittingPolygon } from "./approxFittingPolygon";
import { chainCompoundPrimitiveTransforms } from "./utils";
// TODO: unify with shortestSegmentBetweenShapes
/**
 * Checks if the two shapes are closer than a given value, returns true if they are intersecting.
 *
 * NOTE: This function over approximates to smaller values when it would be too complicated/expensive to
 * compute the actual minimum. This means that the actual distance could be bigger than the returned one,
 * making it safe to use for checking keepouts.
 * Every approximation in the following code is marked with an APPROX comment.
 *
 * NOTE: This function can throw exceptions in some cases, such as computing unsupported shapes distance
 * or when using unsupported matrix transformations (like shear or normalized non-affine).
 */
export function isCloserThanApprox(shapeA, shapeB, minDist) {
    // Should never happen as we call this only with copper shapes which has no expansion
    if (shapeA.type === PrimitiveType.Expansion || shapeB.type === PrimitiveType.Expansion) {
        throw new Error("Expansion shapes not supported yet.");
    }
    // 1. Recurses over compound shapes
    if (shapeA.type === PrimitiveType.Union || shapeA.type === PrimitiveType.Difference) {
        const [transformedA, transformedB] = chainCompoundPrimitiveTransforms(shapeA);
        const resultA = isCloserThanApprox(transformedA, shapeB, minDist);
        const resultB = isCloserThanApprox(transformedB, shapeB, minDist);
        return shapeA.type === PrimitiveType.Union ? resultA || resultB : resultA && !resultB;
    }
    if (shapeB.type === PrimitiveType.Union || shapeB.type === PrimitiveType.Difference) {
        const [transformedA, transformedB] = chainCompoundPrimitiveTransforms(shapeB);
        const resultA = isCloserThanApprox(transformedA, shapeA, minDist);
        const resultB = isCloserThanApprox(transformedB, shapeA, minDist);
        return shapeB.type === PrimitiveType.Union ? resultA || resultB : resultA && !resultB;
    }
    // 2. Sanity checks for transforms, also decomposes the matrices
    if (!mat3.affineSanityCheck(shapeA.transform) || !mat3.affineSanityCheck(shapeB.transform)) {
        throw new Error("Transform matrix of input shapes is not a simple affine.");
    }
    const transformDecomposeA = mat3.decompose(shapeA.transform);
    const transformDecomposeB = mat3.decompose(shapeB.transform);
    if (transformDecomposeA.xyDot > 0 || transformDecomposeB.xyDot > 0) {
        throw new Error("Transform matrix of input shapes has unsupported shear.");
    }
    // To solve this problem we use a multi-stage approach, bailing out with false value if that specific
    // stage indicates that the shapes distance is surely bigger than minDist
    // TODO: evaluate if some stages are useless (aka do not speedup or increase accuracy)
    // 1st pass: Bounding Circle check
    const boundingCircleA = approxBoundingCircle(shapeA, transformDecomposeA);
    const boundingCircleB = approxBoundingCircle(shapeB, transformDecomposeB);
    const boundingCircleDist = boundingCircleDistance(boundingCircleA, boundingCircleB);
    // 2st pass: Bounding Box check
    const boundingBoxA = approxAABoundingBox(shapeA);
    const boundingBoxB = approxAABoundingBox(shapeB);
    const boundingBoxDist = boundingBoxDistance(boundingBoxA, boundingBoxB);
    // We use both checks together in AND, as they are both needed for a collision to happen
    // This is because both bounding shapes fully contain the underlying primitive
    if (boundingCircleDist > minDist && boundingBoxDist > minDist) {
        return false;
    }
    // 3st pass: Fitting polygon check
    // We choose 16 as segments, if it's 8 then numerical errors happen with oblongs
    const fittingPolygonA = approxFittingPolygon(shapeA, 16);
    const fittingPolygonB = approxFittingPolygon(shapeB, 16);
    const dist = geom2.convexPolygonDistance(fittingPolygonA, fittingPolygonB);
    if (!dist) {
        // NOTE: this was added to catch a previous runtime error more explicitly
        throw new Error("Cannot calculate distance to empty polygon.");
    }
    const { minDist: fittingPolygonDist } = dist;
    if (fittingPolygonDist > minDist) {
        return false;
    }
    return true;
}
//# sourceMappingURL=isCloserThanApprox.js.map